import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ArrowLeft, Upload, X, FileText, Image as ImageIcon, ChevronLeft, ChevronRight, Plus, Minus, Calendar } from 'lucide-react';
import { CungVeDichAPI } from '../services/API';
import API from '../services/API';
import ProvincesService from '../services/ProvincesService';
import SystemHeader from './SystemHeader';
import Toast from './Toast';

const ShipmentReportForm = ({ userInfo, accessToken, onLogout, totalPoints = 0 }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [toast, setToast] = useState(null);
  
  // Location states
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [wards, setWards] = useState([]);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingWards, setLoadingWards] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    notes: '',
    status: 1,
    attachmentUrls: [],
    city: '',
    district: '',
    ward: '',
    address: '',
    totalBoxes: 0,
    shipmentDate: ''
  });
  
  const [uploadingImages, setUploadingImages] = useState(false);
  const [previewImageIndex, setPreviewImageIndex] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  // Get reportId from URL params (for edit mode)
  const urlParams = new URLSearchParams(window.location.search);
  const reportId = urlParams.get('reportId');
  const isEditMode = !!reportId;

  useEffect(() => {
    const initializeData = async () => {
      if (accessToken) {
        // Load provinces
        const provincesList = await loadProvinces();
        
        const productsList = await loadProducts();
        
        if (isEditMode && productsList.length > 0) {
          await loadReportData(productsList, provincesList);
        }
      }
    };
    
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, reportId, isEditMode]);

  // Load districts when city changes
  useEffect(() => {
    if (formData.city) {
      loadDistricts(formData.city);
    } else {
      setDistricts([]);
      setWards([]);
      setFormData(prev => ({ ...prev, district: '', ward: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.city]);

  // Load wards when district changes
  useEffect(() => {
    if (formData.district) {
      loadWards(formData.district);
    } else {
      setWards([]);
      setFormData(prev => ({ ...prev, ward: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.district]);

  const handlePrevImage = () => {
    if (previewImageIndex === null) return;
    const urls = formData.attachmentUrls || [];
    setPreviewImageIndex((prev) => 
      prev > 0 ? prev - 1 : urls.length - 1
    );
  };

  const handleNextImage = () => {
    if (previewImageIndex === null) return;
    const urls = formData.attachmentUrls || [];
    setPreviewImageIndex((prev) => 
      prev < urls.length - 1 ? prev + 1 : 0
    );
  };

  // Handle keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (previewImageIndex === null) return;
      
      if (e.key === 'Escape') {
        setPreviewImageIndex(null);
      } else if (e.key === 'ArrowLeft') {
        handlePrevImage();
      } else if (e.key === 'ArrowRight') {
        handleNextImage();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewImageIndex, formData.attachmentUrls?.length || 0]);

  const loadProvinces = async () => {
    try {
      setLoadingProvinces(true);
      const provincesList = await ProvincesService.getProvinces();
      setProvinces(provincesList);
      return provincesList;
    } catch (error) {
      console.error('Error loading provinces:', error);
      setToast({
        message: 'Không thể tải danh sách tỉnh thành. Vui lòng thử lại.',
        type: 'error'
      });
      return [];
    } finally {
      setLoadingProvinces(false);
    }
  };

  const loadDistricts = async (provinceId) => {
    try {
      setLoadingDistricts(true);
      const districtsList = await ProvincesService.getDistrictsByProvince(provinceId);
      setDistricts(districtsList);
      return districtsList;
    } catch (error) {
      console.error('Error loading districts:', error);
      setToast({
        message: 'Không thể tải danh sách quận huyện. Vui lòng thử lại.',
        type: 'error'
      });
      return [];
    } finally {
      setLoadingDistricts(false);
    }
  };

  const loadWards = async (districtId) => {
    try {
      setLoadingWards(true);
      const wardsList = await ProvincesService.getWardsByDistrict(districtId);
      setWards(wardsList);
      return wardsList;
    } catch (error) {
      console.error('Error loading wards:', error);
      setToast({
        message: 'Không thể tải danh sách phường xã. Vui lòng thử lại.',
        type: 'error'
      });
      return [];
    } finally {
      setLoadingWards(false);
    }
  };

  const loadProducts = async () => {
    try {
      setInitialLoading(true);
      const result = await CungVeDichAPI.getProducts(accessToken);
      
      if (result.success && result.products) {
        // Transform products to include quantity field (default 0)
        const transformedProducts = result.products.map(product => ({
          ...product,
          quantity: 0
        }));
        
        // Sort products by name
        const sortedProducts = transformedProducts.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setProducts(sortedProducts);
        return sortedProducts; // Return products for use in loadReportData
      }
      return [];
    } catch (error) {
      console.error('Error loading products:', error);
      setError('Không thể tải danh sách sản phẩm');
      return [];
    } finally {
      setInitialLoading(false);
    }
  };

  const loadReportData = async (productsList, provincesOverride = []) => {
    try {
      setLoading(true);
      const result = await CungVeDichAPI.getShipmentReport(reportId, accessToken);
      
      if (result.success && result.data) {
        const report = result.data;
        
        // Check if status is not "Chờ xử lý" (1), redirect to detail page
        if (report.status !== 1) {
          setToast({
            message: '⚠️ Không thể chỉnh sửa phiếu trả hàng với trạng thái hiện tại',
            type: 'warning'
          });
          
          setTimeout(() => {
            navigate(`/shipment-report-detail?reportId=${reportId}&${urlParams.toString()}`);
          }, 1500);
          return;
        }
        
        // Use productsList if provided, otherwise use current products state
        const currentProducts = productsList || products;
        
        // Map report items to products array
        const updatedProducts = currentProducts.map(product => {
          const reportItem = report.items?.find(item => item.productId === product.id);
          return reportItem 
            ? { ...product, quantity: reportItem.quantity || 0 }
            : { ...product, quantity: 0 };
        });
        
        setProducts(updatedProducts);
        
        // Map stored names/codes to current select options
        const normalizedValue = (value) => (value ? String(value) : '');
        const provincesSource = (provincesOverride && provincesOverride.length > 0) ? provincesOverride : provinces;
        const cityValue = normalizedValue(report.city);
        
        const cityOption = provincesSource.find(
          (province) => String(province.code) === cityValue || province.name === cityValue
        );
        const cityCode = cityOption ? String(cityOption.code) : '';
        
        let districtsList = [];
        if (cityCode) {
          districtsList = await loadDistricts(cityCode) || [];
        }
        
        const districtValue = normalizedValue(report.district);
        const districtOption = (districtsList.length ? districtsList : districts).find(
          (district) => String(district.code) === districtValue || district.name === districtValue
        );
        const districtCode = districtOption ? String(districtOption.code) : '';
        
        let wardsList = [];
        if (districtCode) {
          wardsList = await loadWards(districtCode) || [];
        }
        
        const wardValue = normalizedValue(report.ward);
        const wardOption = (wardsList.length ? wardsList : wards).find(
          (ward) => String(ward.code) === wardValue || ward.name === wardValue
        );
        const wardCode = wardOption ? String(wardOption.code) : '';
        
        setFormData({
          notes: report.notes || '',
          status: report.status || 1,
          attachmentUrls: report.attachmentUrls || [],
          city: cityCode,
          district: districtCode,
          ward: wardCode,
          address: report.address || '',
          totalBoxes: report.totalBoxes || 0,
          shipmentDate: report.shipmentDate ? new Date(report.shipmentDate).toISOString().split('T')[0] : ''
        });
      }
    } catch (error) {
      console.error('Error loading report:', error);
      setError(error.message || 'Không thể tải dữ liệu trả hàng');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (productId, value) => {
    // Allow empty string or valid number during typing
    if (value === '' || value === null || value === undefined) {
      setProducts(prevProducts =>
        prevProducts.map(product =>
          product.id === productId ? { ...product, quantity: '' } : product
        )
      );
      return;
    }
    
    // Only allow digits
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 0) {
      // If invalid, keep current value or set to empty
      return;
    }
    
    setProducts(prevProducts =>
      prevProducts.map(product =>
        product.id === productId ? { ...product, quantity: numValue } : product
      )
    );
  };

  const handleQuantityBlur = (productId, value) => {
    // Normalize to 0 if invalid or empty
    let normalizedValue = 0;
    if (value !== '' && value !== null && value !== undefined) {
      const numValue = parseInt(value);
      if (!isNaN(numValue) && numValue >= 0) {
        normalizedValue = numValue;
      }
    }
    
    setProducts(prevProducts =>
      prevProducts.map(product =>
        product.id === productId ? { ...product, quantity: normalizedValue } : product
      )
    );
  };

  const handleImageClick = (imageUrl, productName, productDescription) => {
    setSelectedImage({ url: imageUrl, name: productName, description: productDescription });
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Auto upload immediately after selection
    try {
      setUploadingImages(true);
      const uploadedUrls = [];
      
      for (const file of files) {
        console.log('Auto-uploading file:', file.name);
        const result = await API.uploadImage(file);
        console.log('Upload result:', result);
        if (result.success && result.fileUrl) {
          uploadedUrls.push(result.fileUrl);
          console.log('Uploaded URL:', result.fileUrl);
        }
      }
      
      console.log('All auto-uploaded URLs:', uploadedUrls);
      
      // Update attachmentUrls with newly uploaded files
      setFormData(prev => {
        const newAttachmentUrls = [...prev.attachmentUrls, ...uploadedUrls];
        console.log('Updated attachmentUrls after auto-upload:', newAttachmentUrls);
        return {
          ...prev,
          attachmentUrls: newAttachmentUrls
        };
      });
      
      // Show success toast
      setToast({
        message: `✅ Upload thành công ${uploadedUrls.length} ảnh`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error auto-uploading images:', error);
      setToast({
        message: 'Có lỗi xảy ra khi upload ảnh: ' + error.message,
        type: 'error'
      });
    } finally {
      setUploadingImages(false);
      // Clear file input
      e.target.value = '';
    }
  };

  const handleRemoveUploadedImage = (url) => {
    setFormData(prev => ({
      ...prev,
      attachmentUrls: prev.attachmentUrls.filter(u => u !== url)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get items with quantity > 0 (handle empty string as 0)
    const items = products
      .filter(product => {
        const qty = product.quantity === '' ? 0 : (product.quantity || 0);
        return qty > 0;
      })
      .map(product => ({
        productId: product.id,
        quantity: product.quantity === '' ? 0 : (product.quantity || 0)
      }));
    
    // Validation
    if (items.length === 0) {
      setToast({
        message: 'Vui lòng nhập số lượng cho ít nhất một sản phẩm',
        type: 'warning'
      });
      return;
    }
    
    // Validate required fields
    if (!formData.city) {
      setToast({
        message: 'Vui lòng chọn Tỉnh/Thành phố',
        type: 'warning'
      });
      return;
    }
    
    if (!formData.district) {
      setToast({
        message: 'Vui lòng chọn Quận/Huyện',
        type: 'warning'
      });
      return;
    }
    
    if (!formData.ward) {
      setToast({
        message: 'Vui lòng chọn Phường/Xã',
        type: 'warning'
      });
      return;
    }
    
    if (!formData.address || formData.address.trim() === '') {
      setToast({
        message: 'Vui lòng nhập Địa chỉ chi tiết',
        type: 'warning'
      });
      return;
    }
    
    if (!formData.shipmentDate) {
      setToast({
        message: 'Vui lòng chọn Ngày gửi hàng',
        type: 'warning'
      });
      return;
    }
    
    if (!formData.totalBoxes || formData.totalBoxes <= 0) {
      setToast({
        message: 'Vui lòng nhập Tổng số thùng hàng (lớn hơn 0)',
        type: 'warning'
      });
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const selectedCity = provinces.find((province) => String(province.code) === String(formData.city));
      const selectedDistrict = districts.find((district) => String(district.code) === String(formData.district));
      const selectedWard = wards.find((ward) => String(ward.code) === String(formData.ward));

      const submitData = {
        items: items,
        notes: formData.notes || null,
        status: formData.status,
        attachmentUrls: formData.attachmentUrls,
        city: selectedCity?.name || formData.city || null,
        district: selectedDistrict?.name || formData.district || null,
        ward: selectedWard?.name || formData.ward || null,
        address: formData.address.trim(),
        totalBoxes: formData.totalBoxes || 0,
        shipmentDate: formData.shipmentDate || null
      };
      
      console.log('=== SUBMIT DATA ===');
      console.log('attachmentUrls:', formData.attachmentUrls);
      console.log('Full submit data:', submitData);
      console.log('==================');
      
      let result;
      if (isEditMode) {
        result = await CungVeDichAPI.updateShipmentReport(reportId, submitData, accessToken);
      } else {
        result = await CungVeDichAPI.createShipmentReport(submitData, accessToken);
      }
      
      if (result.success) {
        // Show success toast
        setToast({
          message: isEditMode ? '✅ Cập nhật phiếu trả hàng thành công!' : '✅ Tạo phiếu trả hàng thành công!',
          type: 'success'
        });
        
        // Navigate back to list after a short delay
        setTimeout(() => {
          urlParams.delete('reportId');
          navigate(`/tra-hang?${urlParams.toString()}`);
        }, 1500);
      } else {
        throw new Error(result.message || 'Có lỗi xảy ra');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setToast({
        message: error.message || 'Có lỗi xảy ra khi lưu phiếu trả hàng',
        type: 'error'
      });
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (isEditMode) {
      navigate(`/shipment-report-detail?reportId=${reportId}&${urlParams.toString()}`);
    } else {
      urlParams.delete('reportId');
      navigate(`/shipment-reports?${urlParams.toString()}`);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      <SystemHeader 
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
      />
      
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Quay lại</span>
          </button>
          
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500 rounded-lg">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditMode ? `Chỉnh sửa trả hàng #${reportId}` : 'Tạo trả hàng mới'}
              </h1>
              <p className="text-sm text-gray-600">
                {isEditMode ? 'Cập nhật thông tin trả hàng' : 'Điền thông tin chi tiết cho phiếu trả hàng mới'}
              </p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Products Section - Table View */}
          <div className="bg-white rounded-xl shadow-sm">
            {/* Summary Statistics - Compact */}
            <div className="py-2 bg-blue-50">
              <div className="grid grid-cols-2 gap-3 px-4">
                <div className="bg-white rounded p-3 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                        <Package className="w-3 h-3 text-blue-600" />
                      </div>
                      <span className="text-xs font-medium text-gray-500">Số sản phẩm</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {products.filter(p => (p.quantity || 0) > 0).length}
                    </span>
                  </div>
                </div>
                <div className="bg-white rounded p-3 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                        <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-xs font-medium text-gray-500">Tổng số lượng</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {products.reduce((total, product) => {
                        const qty = product.quantity === '' ? 0 : (product.quantity || 0);
                        return total + qty;
                      }, 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Products Table */}
            <div className="overflow-x-auto">
              {initialLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-3 text-gray-600">Đang tải danh sách sản phẩm...</span>
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-10 px-4">
                  <p className="text-gray-500">Không có sản phẩm nào để hiển thị</p>
                </div>
              ) : (
                <div className="overflow-y-auto max-h-[calc(100vh-500px)]">
                  <table className="w-full border-collapse">
                    <thead className="bg-gray-50 sticky top-0 z-50 shadow-lg" style={{ position: 'sticky', top: 0 }}>
                      <tr>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300 bg-gray-50">
                          Sản phẩm
                        </th>
                        <th className="px-1 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300 w-20 bg-gray-50">
                          SL
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {products.map((product) => (
                        <tr key={product.id} className="hover:bg-gray-50">
                          {/* Product Info */}
                          <td className="px-2 py-2">
                            <div className="flex items-center space-x-2">
                              {product.imageUrl && (
                                <div
                                  className="w-8 h-8 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => handleImageClick(product.imageUrl, product.name, product.description)}
                                >
                                  <img
                                    src={product.imageUrl}
                                    alt={product.name}
                                    className="w-full h-full object-cover rounded border border-gray-200"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                              <div className="text-xs font-medium text-gray-900 leading-tight flex-1" title={product.name}>
                                <div
                                  className="overflow-hidden"
                                  style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    maxHeight: '2.5em'
                                  }}
                                >
                                  {product.name}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Quantity Input */}
                          <td className="px-1 py-2">
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={product.quantity === '' ? '' : (product.quantity || 0)}
                              style={{ width: '60px', minHeight: '30px', fontSize: '16px' }}
                              onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                              onBlur={(e) => handleQuantityBlur(product.id, e.target.value)}
                              onFocus={(e) => e.target.select()}
                              className="px-1 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-center"
                              placeholder="0"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Address Information - Required Fields */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Thông tin địa chỉ</h3>
            
            <div className="space-y-4">
              {/* City/Province */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tỉnh/Thành phố <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value, district: '', ward: '' })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                  style={{ fontSize: '16px', minHeight: '48px' }}
                  required
                  disabled={loadingProvinces}
                >
                  <option value="">Chọn tỉnh/thành phố</option>
                  {provinces.map((province) => (
                    <option key={province.code} value={province.code}>
                      {province.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* District */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quận/Huyện <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.district}
                  onChange={(e) => setFormData({ ...formData, district: e.target.value, ward: '' })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                  style={{ fontSize: '16px', minHeight: '48px' }}
                  required
                  disabled={!formData.city || loadingDistricts}
                >
                  <option value="">Chọn quận/huyện</option>
                  {districts.map((district) => (
                    <option key={district.code} value={district.code}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ward */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phường/Xã <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.ward}
                  onChange={(e) => setFormData({ ...formData, ward: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                  style={{ fontSize: '16px', minHeight: '48px' }}
                  required
                  disabled={!formData.district || loadingWards}
                >
                  <option value="">Chọn phường/xã</option>
                  {wards.map((ward) => (
                    <option key={ward.code} value={ward.code}>
                      {ward.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Detailed Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Địa chỉ chi tiết <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Nhập địa chỉ chi tiết..."
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  style={{ fontSize: '16px', minHeight: '48px' }}
                  required
                />
              </div>
            </div>
          </div>

          {/* Shipment Date & Total Boxes */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Shipment Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ngày gửi hàng <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                    <Calendar className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    value={formData.shipmentDate}
                    onChange={(e) => setFormData({ ...formData, shipmentDate: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                    style={{ 
                      fontSize: '16px',
                      minHeight: '48px',
                      WebkitAppearance: 'none',
                      appearance: 'none'
                    }}
                    required
                  />
                </div>
              </div>

              {/* Total Boxes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tổng số thùng hàng <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const currentValue = formData.totalBoxes || 0;
                      if (currentValue > 0) {
                        setFormData({ ...formData, totalBoxes: currentValue - 1 });
                      }
                    }}
                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!formData.totalBoxes || formData.totalBoxes <= 0}
                  >
                    <Minus className="w-4 h-4 text-gray-700" />
                  </button>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formData.totalBoxes === 0 ? '' : formData.totalBoxes}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === null || value === undefined) {
                        setFormData({ ...formData, totalBoxes: 0 });
                      } else {
                        const numValue = parseInt(value);
                        if (!isNaN(numValue) && numValue >= 0) {
                          setFormData({ ...formData, totalBoxes: numValue });
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === null || value === undefined) {
                        setFormData({ ...formData, totalBoxes: 0 });
                      } else {
                        const numValue = parseInt(value);
                        if (!isNaN(numValue) && numValue >= 0) {
                          setFormData({ ...formData, totalBoxes: numValue });
                        } else {
                          setFormData({ ...formData, totalBoxes: 0 });
                        }
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-center"
                    style={{ fontSize: '16px', minHeight: '40px' }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const currentValue = formData.totalBoxes || 0;
                      setFormData({ ...formData, totalBoxes: currentValue + 1 });
                    }}
                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors"
                  >
                    <Plus className="w-4 h-4 text-gray-700" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4" />
              Ghi chú
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              placeholder="Nhập ghi chú (tùy chọn)..."
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
            />
          </div>

          {/* Attachments */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <ImageIcon className="w-4 h-4" />
                Ảnh đính kèm
                <span className="text-xs text-gray-500 font-normal">(Tùy chọn)</span>
              </label>
              {(formData.attachmentUrls || []).length > 0 && (
                <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                  {(formData.attachmentUrls || []).length} ảnh
                </span>
              )}
            </div>
            
            {/* Upload Area with Drag & Drop Visual */}
            {uploadingImages ? (
              <div className="mb-4 p-8 border-2 border-dashed border-blue-400 bg-blue-50 rounded-xl">
                <div className="flex flex-col items-center justify-center">
                  <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                  <p className="text-sm font-medium text-blue-700">Đang upload ảnh...</p>
                  <p className="text-xs text-blue-600 mt-1">Vui lòng đợi</p>
                </div>
              </div>
            ) : (
              <div className="mb-4">
                <label className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-gradient-to-br hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 group">
                  <div className="flex flex-col items-center justify-center">
                    <div className="relative mb-3">
                      <div className="p-3 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl group-hover:from-blue-100 group-hover:to-indigo-100 transition-all duration-200">
                        <Upload className="w-6 h-6 text-gray-500 group-hover:text-blue-600 transition-colors" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 font-semibold mb-1">Nhấn để chọn ảnh</p>
                    <p className="text-xs text-gray-500">hoặc kéo thả vào đây</p>
                    <p className="text-xs text-gray-400 mt-2">PNG, JPG, GIF • Tối đa 5MB mỗi file</p>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    disabled={uploadingImages}
                    className="hidden"
                  />
                </label>
              </div>
            )}
            
            {/* Uploaded Images Grid */}
            {(formData.attachmentUrls || []).length > 0 && (
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {(formData.attachmentUrls || []).map((url, index) => (
                    <div key={index} className="relative group">
                      <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 border-2 border-gray-200 group-hover:border-blue-400 transition-all duration-200">
                        <img
                          src={url}
                          alt={`Ảnh ${index + 1}`}
                          className="w-full h-full object-cover cursor-pointer group-hover:scale-110 transition-transform duration-200"
                          onClick={() => setPreviewImageIndex(index)}
                        />
                        {/* Overlay on hover */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <button
                              type="button"
                              onClick={() => setPreviewImageIndex(index)}
                              className="p-2 bg-white rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                              title="Xem ảnh"
                            >
                              <ImageIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* Remove button */}
                      <button
                        type="button"
                        onClick={() => handleRemoveUploadedImage(url)}
                        className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-200 transform hover:scale-110"
                        title="Xóa ảnh"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      {/* Image number badge */}
                      <div className="absolute top-2 left-2 px-2 py-0.5 bg-black bg-opacity-60 text-white text-xs font-medium rounded-full">
                        {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 -mx-4 md:-mx-6 px-4 md:px-6 py-4 mt-6 flex gap-3 justify-end">
            <button
              type="button"
              onClick={handleBack}
              disabled={loading}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium text-sm transition-colors"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={loading || products.filter(p => {
                const qty = p.quantity === '' ? 0 : (p.quantity || 0);
                return qty > 0;
              }).length === 0}
              className="px-8 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm shadow-md hover:shadow-lg transition-all"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Đang xử lý...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isEditMode ? '💾 Cập nhật' : '✨ Tạo mới'}
                </span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">{selectedImage.name}</h3>
                <button
                  onClick={closeImageModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4">
              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                className="w-full h-auto max-h-[60vh] object-contain rounded-lg mb-4"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                }}
              />
              {selectedImage.description && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Mô tả sản phẩm:</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {selectedImage.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Lightbox */}
      {previewImageIndex !== null && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center p-4 z-50 animate-fade-in"
          onClick={() => setPreviewImageIndex(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setPreviewImageIndex(null)}
            className="absolute top-4 right-4 p-3 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-all z-20 backdrop-blur-sm"
            title="Đóng (ESC)"
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Image counter */}
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm z-20">
            {previewImageIndex + 1} / {(formData.attachmentUrls || []).length}
          </div>
          
          {/* Previous button */}
          {(formData.attachmentUrls || []).length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                console.log('Previous clicked, current index:', previewImageIndex);
                handlePrevImage();
              }}
              className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 p-3 sm:p-4 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full transition-all z-20 backdrop-blur-sm shadow-xl hover:scale-110"
              title="Ảnh trước (←)"
            >
              <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8 stroke-[3]" />
            </button>
          )}
          
          {/* Next button */}
          {(formData.attachmentUrls || []).length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                console.log('Next clicked, current index:', previewImageIndex);
                handleNextImage();
              }}
              className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 p-3 sm:p-4 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full transition-all z-20 backdrop-blur-sm shadow-xl hover:scale-110"
              title="Ảnh tiếp theo (→)"
            >
              <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8 stroke-[3]" />
            </button>
          )}
          
          {/* Image */}
          <div className="relative max-w-6xl max-h-[85vh] w-full h-full flex items-center justify-center">
            <img
              src={(formData.attachmentUrls || [])[previewImageIndex]}
              alt={`Ảnh ${previewImageIndex + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          {/* Instructions */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white px-4 py-2 rounded-full text-xs backdrop-blur-sm">
            <span className="hidden sm:inline">← → để điều hướng • </span>ESC hoặc click bên ngoài để đóng
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentReportForm;

